import React, { useState, useEffect, useRef } from "react";
import { GoogleIcon, ICON_SIZES } from "../icons/google-icon";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { SxProps } from "@mui/material";
import { isModuleNamespaceObject } from "util/types";

interface TextInputProps {
  id: string;
  label: string;
  value: string;
  onChange: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  fullWidth?: boolean;
  error?: string | null;
  iconID?: number;
  type?: string;
  disabled?: boolean;
  multiline?: boolean;
  maxRows?: number;
  autoFocus?: boolean;
  sx?: SxProps;
  rows?: number;
  autoBlurAfterChange?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const TextInput = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  TextInputProps
>((props, ref) => {
  const [internalValue, setInternalValue] = useState(props.value ?? "");
  const textFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local state in sync if parent changes the value externally
  useEffect(() => {
    if (props.value !== internalValue) {
      setInternalValue(props.value ?? "");
    }
  }, [props.value]);

  useEffect(() => {
    if (props.autoBlurAfterChange) {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      blurTimeoutRef.current = setTimeout(() => {
        if (textFieldRef.current && document.activeElement === textFieldRef.current) {
          textFieldRef.current.blur();
        }
      }, 1500);

      return () => {
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
        }
      };
    }
  }, [internalValue, props.autoBlurAfterChange])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (props.type === "number") {
      const parsed = Number(e.target.value);
      if (isNaN(parsed)) {
        setInternalValue(internalValue);
        return;
      }
    }
    setInternalValue(e.target.value);
    props.onChange?.(e as any);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.onChange?.({
      ...e,
      target: { ...e.target, value: internalValue },
    });
    props.onBlur?.(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (props.onKeyDown) {
      props.onKeyDown(e as any);
    }
  };

  return (
    <TextField
      inputRef={(el) => {
        textFieldRef.current = el;
        if (typeof ref === "function") ref(el);
        else if (ref) (ref as React.MutableRefObject<any>).current = el;
      }}
      id={props.id}
      label={props.label}
      disabled={props.disabled ?? false}
      variant="outlined"
      type={props.type === "number" ? "text" : props.type ?? "text"}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      margin="normal"
      fullWidth
      error={!!props.error}
      helperText={props.error}
      autoFocus={props.autoFocus ?? false}
      value={internalValue}
      multiline={props.multiline ?? false}
      rows={props.rows}
      maxRows={props.multiline ? props.maxRows ?? Infinity : undefined}
      sx={props.sx}
      inputProps={{
        pattern: props.type === "number" ? "[0-9]*\\.?[0-9]*" : undefined,
        inputMode: props.type === "number" ? "decimal" : undefined,
      }}
      InputProps={{
        startAdornment:
          props.iconID !== undefined ? (
            <InputAdornment position="start">
              <GoogleIcon size={ICON_SIZES.Medium} id={props.iconID} />
            </InputAdornment>
          ) : null,
      }}
    />
  );
});

export default TextInput;
